import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../context/authContext';
import { toast } from 'react-toastify';

// Configuración de la API
//const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const useNotificaciones = (socket) => {
    const { user, role, roles } = useContext(AuthContext);
    const [notificacionesNoLeidas, setNotificacionesNoLeidas] = useState(0);
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(false);

    // Multi-rol (074): un usuario recibe una notificación si ALGUNO de sus roles
    // matchea (no solo el principal 'role'). El admin recibe siempre. El último
    // 'return role === rolDestino' es el respaldo para sesiones viejas sin el key
    // 'roles' (authContext las hidrata con [role], pero acá no conviene confiar).
    const puedoRecibir = useCallback(
        (rolDestino) => {
            if (role === 'admin') return true;
            if (Array.isArray(roles) && roles.includes(rolDestino)) return true;
            return role === rolDestino;
        },
        [role, roles]
    );

    // Ids de las notificaciones ya en la lista. Se consulta desde agregarNotificacion
    // para poder descartar duplicados ANTES de tocar el contador de no leídas: con un
    // setState updater no alcanza, porque el updater corre después y el contador ya
    // había subido. También se llena al cargar desde el backend, para que un aviso que
    // ya está en la lista al abrir la página no se sume dos veces.
    const idsNotificaciones = useRef(new Set());

    // Obtener token de autenticación
    const getToken = () => {
        const rawToken = localStorage.getItem('token');
        return rawToken ? JSON.parse(rawToken) : null;
    };

    // Función para hacer peticiones a la API
    const apiRequest = useCallback(async (endpoint, options = {}) => {
        const token = getToken();
        if (!token) return null;

        try {
            const response = await fetch(`${endpoint}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...options.headers
                },
                ...options
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error en API request:', error);
            return null;
        }
    }, []);

    // Cargar notificaciones desde el backend
    const cargarNotificaciones = useCallback(async (soloNoLeidas = false) => {
        if (!user || !role) return;
        
        setLoading(true);
        try {
            const endpoint = soloNoLeidas 
                ? `/api/notificaciones/no-leidas`
                : `/api/notificaciones?limite=50&offset=0${soloNoLeidas ? '&soloNoLeidas=true' : ''}`;
            
            const result = await apiRequest(endpoint);
            if (result && result.success) {
                if (soloNoLeidas) {
                    setNotificacionesNoLeidas(result.data.noLeidas);
                } else {
                    setNotificaciones(result.data || []);
                    idsNotificaciones.current = new Set((result.data || []).map(n => n.id));
                    // Calcular no leídas del array completo
                    const noLeidasCount = (result.data || []).filter(n => !n.leida).length;
                    setNotificacionesNoLeidas(noLeidasCount);
                    console.log('🔍 Notificaciones cargadas - Total:', result.data?.length, 'No leídas:', noLeidasCount);
                }
            }
        } catch (error) {
            console.error('Error cargando notificaciones:', error);
        } finally {
            setLoading(false);
        }
    }, [user, role, apiRequest]);

    // Cargar notificaciones al iniciar
    useEffect(() => {
        cargarNotificaciones();
        cargarNotificaciones(true); // Cargar contador de no leídas
    }, [cargarNotificaciones]);

    // Agregar nueva notificación (desde socket)
    //
    // El backend emite DOS eventos por aviso: 'notificacion_nueva' y el específico
    // ('pago_realizado' / 'certificacion_cargada'), y los dos llevan el mismo id de
    // fila. Sin esta guarda, un solo pago se agregaba dos veces a la lista y el
    // contador de no leídas subía 2.
    const agregarNotificacion = useCallback((notificacion) => {
        const nuevaNotificacion = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            leida: false,
            ...notificacion
        };

        // El backend emite los dos eventos con el mismo id de fila, así que el segundo
        // que llega es el mismo aviso. Si lo descartamos acá, el contador de no leídas
        // sube 1 sola vez y no se muestra un toast repetido.
        if (idsNotificaciones.current.has(nuevaNotificacion.id)) {
            console.log('🔍 Notificación duplicada, se ignora. id:', nuevaNotificacion.id);
            return;
        }
        idsNotificaciones.current.add(nuevaNotificacion.id);

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);
        setNotificacionesNoLeidas(prev => prev + 1);

        // Mostrar toast online
        toast.info(`${notificacion.titulo}: ${notificacion.mensaje}`, {
            toastId: `notif_${nuevaNotificacion.id}`,
            onClick: () => marcarComoLeida(nuevaNotificacion.id)
        });
    }, []);

    // Marcar notificación como leída
    const marcarComoLeida = useCallback(async (id) => {
        console.log('🔍 useNotificaciones Debug - marcarComoLeida llamado con ID:', id);
        console.log('  - notificacionesNoLeidas ANTES:', notificacionesNoLeidas);
        
        try {
            console.log('🔍 Enviando request a /api/notificaciones/marcar-leidas');
            const result = await apiRequest('/api/notificaciones/marcar-leidas', {
                method: 'PATCH',
                body: JSON.stringify({ ids: [id] })
            });
            console.log('🔍 Resultado del API:', result);

            if (result && result.success) {
                console.log('✅ API exitoso, actualizando estado local');
                setNotificaciones(prev => {
                    const actualizadas = prev.map(n => 
                        n.id === id ? { ...n, leida: true } : n
                    );
                    console.log('🔍 Notificaciones actualizadas:', actualizadas.filter(n => !n.leida).length, 'no leídas');
                    return actualizadas;
                });
                setNotificacionesNoLeidas(prev => {
                    const nuevoValor = Math.max(0, prev - 1);
                    console.log('🔍 notificacionesNoLeidas DESPUÉS:', nuevoValor);
                    return nuevoValor;
                });
            } else {
                console.log('❌ API devolvió error o sin success');
            }
        } catch (error) {
            console.error('❌ Error marcando notificación como leída:', error);
        }
    }, [apiRequest, notificacionesNoLeidas]);

    // Marcar todas como leídas
    const marcarTodasComoLeidas = useCallback(async () => {
        const idsNoLeidas = notificaciones
            .filter(n => !n.leida)
            .map(n => n.id);

        if (idsNoLeidas.length === 0) return;

        try {
            const result = await apiRequest('/api/notificaciones/marcar-leidas', {
                method: 'PATCH',
                body: JSON.stringify({ ids: idsNoLeidas })
            });

            if (result && result.success) {
                setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
                setNotificacionesNoLeidas(0);
            }
        } catch (error) {
            console.error('Error marcando todas como leídas:', error);
        }
    }, [notificaciones, apiRequest]);

    // Escuchar eventos de socket para notificaciones específicas
    useEffect(() => {
        if (!socket) return;

// Notificación general nueva
    const handleNotificacionNueva = (payload) => {
            console.log('🔔 Frontend recibió notificación:', payload);
            console.log('🔔 Rol actual:', role, 'roles:', roles);
            console.log('🔔 ¿Debe procesar?', puedoRecibir(payload.rol_destino));

            // Solo procesar si es para el rol del usuario actual
            if (puedoRecibir(payload.rol_destino)) {
                console.log('✅ Procesando notificación para rol:', role);
                agregarNotificacion({
                    // El id de la fila y la fecha tienen que viajar. Sin el id, usar el
                    // Date.now() de agregarNotificacion y (a) el mismo aviso entraba dos
                    // veces, porque el backend manda 'notificacion_nueva' y el específico;
                    // y (b) al hacer clic se mandaba un id que no existe en la BD, así que
                    // marcarComoLeida no marcaba nada.
                    id: payload.id,
                    fecha_creacion: payload.fecha_creacion,
                    tipo: payload.tipo,
                    titulo: payload.titulo,
                    mensaje: payload.mensaje,
                    id_partido_obra: payload.id_partido_obra,
                    datos: payload.datos
                });
            } else {
                console.log('❌ Ignorando notificación - rol destino:', payload.rol_destino, 'mis roles:', roles);
            }
        };

        // Notificación cuando legal o contable realiza un pago
        const handlePagoRealizado = (payload) => {
            // Solo notificar a usuarios de seguimiento
            if (puedoRecibir('seguimiento')) {
                agregarNotificacion({
                    id: payload.id,
                    fecha_creacion: payload.fecha_creacion,
                    tipo: 'pago_realizado',
                    titulo: 'Pago Realizado',
                    // El origen puede ser legal o contable, así que no hardcodeamos el rol
                    // en el texto: usamos el que venga en el payload.
                    mensaje: payload.mensaje || `${payload.rol_origen || 'Contable'} ha realizado el pago para "${payload.nombre_obra}"`,
                    id_partido_obra: payload.id_partido_obra,
                    datos: payload.datos
                });
            }
        };

        // Notificación cuando seguimiento carga certificación
        const handleCertificacionCargada = (payload) => {
            // Notificar a quien carga pagos: contable o legal
            if (puedoRecibir('contable') || puedoRecibir('legal')) {
                agregarNotificacion({
                    id: payload.id,
                    fecha_creacion: payload.fecha_creacion,
                    tipo: 'certificacion_cargada',
                    titulo: 'Certificación Cargada',
                    mensaje: payload.mensaje || `Seguimiento ha cargado la certificación para "${payload.nombre_obra}"`,
                    id_partido_obra: payload.id_partido_obra,
                    datos: payload.datos
                });
            }
        };

        socket.on('notificacion_nueva', handleNotificacionNueva);
        socket.on('pago_realizado', handlePagoRealizado);
        socket.on('certificacion_cargada', handleCertificacionCargada);

        return () => {
            socket.off('notificacion_nueva', handleNotificacionNueva);
            socket.off('pago_realizado', handlePagoRealizado);
            socket.off('certificacion_cargada', handleCertificacionCargada);
        };
    }, [socket, role, roles, user, agregarNotificacion, puedoRecibir]);

    // Cargar notificaciones pendientes al montar el componente
    useEffect(() => {
        if (user && role) {
            console.log('🔍 Cargando notificaciones pendientes (no leídas) para:', user, role);
            cargarNotificaciones(true); // Cargar SOLO notificaciones no leídas
        }
    }, [user, role, cargarNotificaciones]);

    // Si no hay socket, devolver funciones vacías
    if (!socket) {
        return {
            notificaciones,
            notificacionesNoLeidas,
            loading,
            cargarNotificaciones: () => {},
            agregarNotificacion: () => {},
            marcarComoLeida: () => {},
            marcarTodasComoLeidas: () => {}
        };
    }

    return {
        notificaciones,
        notificacionesNoLeidas,
        loading,
        cargarNotificaciones,
        agregarNotificacion,
        marcarComoLeida,
        marcarTodasComoLeidas
    };
};
